import React, { useMemo, useState } from "react";
import { a, useSpring, config } from 'react-spring'
import { useOnScreen } from '../Hooks'
import { createSeededRandom, mixSeed } from '../../helpers/seededRandom'

interface BubbleProps {
  x: number,
  y: number,
  size: number,
  selected?: boolean,
  /** Extra entropy when several bubbles share the same coordinates. */
  seed?: number
}

const AnimatedBubble: React.FC<BubbleProps> = ({
  x,
  y,
  size,
  selected,
  seed = 0,
}) => {
  const [ reverse, setReverse ] = useState<boolean>(false)

  // The drift used to come from Math.random() called during render: the server
  // and the first client render disagreed on the spring, and every re-render
  // restarted the animation. Seeding from the props keeps it identical on both
  // sides and stable.
  const { duration, to } = useMemo(() => {
    const random = createSeededRandom(mixSeed(x, y, size, seed))
    // 50% chance negative, clamped between -50 & 50
    const getRand = () => Math.floor((random() - 0.5) * 50)
    const step = () => ({ transform: `translate3d(${x + getRand()}px,${y + getRand()}px,0)` })
    return {
      duration: 2000 + (getRand() * 2),
      to: [
        step(),
        step(),
        step(),
        step(),
        step(),
        step(),
        step(),
        { transform: `translate3d(${x}px,${y}px,0)` },
      ],
    }
  }, [x, y, size, seed])

  const spring = useSpring({
    config: { ...config.molasses, duration },
    from: { transform: `translate3d(${x}px,${y}px,0)`},
    to,
    onRest: () => {setReverse(!reverse)},
    loop: true,
    reverse: reverse,
  })

  return (
    <a.div className={`absolute rounded-full opacity-20 z-10 ${ selected ? 'bg-red' : 'bg-purple' }`} style={{...spring, width: size, height: size}} />
  )
}

export default AnimatedBubble
